import { useQuery } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { EmptyState, ErrorState } from '@bb/ui';
import type { EventLogFilters } from '../../api/client.ts';
import { EventLogStream } from './EventLogStream.tsx';

function Rejections({ environmentId }: { environmentId: string }) {
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/event-log' });
  const [days, setDays] = useState(7);
  const list = useQuery({ queryKey: ['rejections', environmentId, days], queryFn: () => api.rejections(environmentId, days) });
  return (
    <section aria-label="Rejections">
      <p className="hint">Rejected operations and batches of all traffic, by code and version, with the latest example.</p>
      <select aria-label="Period" value={days} onChange={(e) => setDays(Number(e.target.value))}>
        <option value={1}>Today</option>
        <option value={7}>7 days</option>
        <option value={30}>30 days</option>
      </select>
      {list.isPending ? (
        <p aria-busy="true">Loading…</p>
      ) : list.isError ? (
        <ErrorState title="Rejections could not be loaded" message="Try again." onRetry={() => void list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <EmptyState title="No rejections" description="Every operation in this period was accepted." />
      ) : (
        <table className="table" aria-label="Rejections by code">
          <thead>
            <tr>
              <th scope="col">Code</th>
              <th scope="col">Source</th>
              <th scope="col">App / SDK</th>
              <th scope="col">Count</th>
              <th scope="col">Last seen</th>
              <th scope="col">Example</th>
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((r) => (
              <tr key={`${r.surface}:${r.code}:${r.appInstanceId}:${r.appVersion}:${r.sdkVersion}`}>
                <td>
                  <code className="tone-danger">{r.code}</code>
                </td>
                <td>{r.surface === 'sdk' ? 'SDK' : 'Server'}</td>
                <td>{r.surface === 'sdk' ? `${r.appVersion} / ${r.sdkVersion}` : '—'}</td>
                <td>{r.count}</td>
                <td>{r.lastSeenAt.slice(0, 16).replace('T', ' ')} UTC</td>
                <td>{r.lastSample ? <pre>{JSON.stringify(r.lastSample)}</pre> : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/**
 * Settings → Event User Log (BB-35): raw batches of test profiles (groups with «Event log») and of debug sessions,
 * live; and rejections of all traffic.
 */
export function EventLogPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/event-log' });
  const [tab, setTab] = useState<'log' | 'rejections'>('log');
  const [form, setForm] = useState({ surface: '', externalId: '', installationId: '', code: '' });
  const [filters, setFilters] = useState<Omit<EventLogFilters, 'after'>>({});
  useEffect(() => {
    const timer = setTimeout(
      () =>
        setFilters({
          ...(form.surface ? { surface: form.surface as 'sdk' | 'server' } : {}),
          ...(form.externalId.trim() ? { externalId: form.externalId.trim() } : {}),
          ...(form.installationId.trim() ? { installationId: form.installationId.trim() } : {}),
          ...(form.code.trim() ? { code: form.code.trim() } : {}),
        }),
      300,
    );
    return () => clearTimeout(timer);
  }, [form]);
  return (
    <section>
      <h1>Event User Log</h1>
      <div role="tablist" aria-label="Event log views">
        <button type="button" role="tab" aria-selected={tab === 'log'} onClick={() => setTab('log')}>
          Live log
        </button>
        <button type="button" role="tab" aria-selected={tab === 'rejections'} onClick={() => setTab('rejections')}>
          Rejections
        </button>
      </div>
      {tab === 'log' ? (
        <>
          <div className="filters" role="group" aria-label="Filters">
            <select aria-label="Source" value={form.surface} onChange={(e) => setForm({ ...form, surface: e.target.value })}>
              <option value="">SDK and server</option>
              <option value="sdk">SDK</option>
              <option value="server">Server</option>
            </select>{' '}
            <input aria-label="External ID" placeholder="External ID" value={form.externalId} onChange={(e) => setForm({ ...form, externalId: e.target.value })} />{' '}
            <input aria-label="Installation ID" placeholder="Installation ID" value={form.installationId} onChange={(e) => setForm({ ...form, installationId: e.target.value })} />{' '}
            <input aria-label="Error code" placeholder="Error code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          </div>
          <EventLogStream environmentId={environmentId} filters={filters} />
        </>
      ) : (
        <Rejections environmentId={environmentId} />
      )}
    </section>
  );
}
